import { useAuth0 } from '@auth0/auth0-react'
import { LS_REVALIDATION_KEY } from '@constants'
import { TemplateType, useTemplatesLazyQuery } from '@generated/graphql'
import StorageHelper from '@modules/storage'
import { setEditorTemplate } from '@store/slices/editor'
import { editorTemplateSelector } from '@store/slices/editor/selectors'
import { currentNetworkIdSelector } from '@store/slices/networks/selectors'
import { setTemplates, setTemplatesLoading } from '@store/slices/templates'
import { getTemplateByTypeAndSubtype } from '@store/slices/templates/helpers'
import { templatesSelector } from '@store/slices/templates/selectors'
import { useAppDispatch, useAppSelector } from '@store/typedHooks'
import { Modal } from 'antd'
import localforage from 'localforage'
import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

const CANONICAL_NETWORK_ID = 'rec4tMkSLWxeAanU9'

export function useFetchTemplates(types: TemplateType[]) {
  const { isAuthenticated } = useAuth0()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const networkId = useAppSelector(currentNetworkIdSelector)
  const templates = useAppSelector(templatesSelector)
  const template = useAppSelector(editorTemplateSelector)
  const { type: _subtype } = useParams<{ type?: string }>()
  const subtype = !_subtype || _subtype === 'default' ? null : _subtype

  useEffect(() => {
    if (types?.[0]) {
      StorageHelper.setItem('templateType', types?.[0])
    }
  }, [types?.[0]])

  const [retryCount, setRetryCount] = useState(0)
  const MAX_RETRIES = 3
  const RETRY_DELAY = 2000 // 2 seconds

  useEffect(() => {
    const templateType = types?.[0]
    if (!templates || !templates.length || !templateType) {
      return
    }

    const _template = getTemplateByTypeAndSubtype({
      templates,
      type: templateType,
      subtype,
      currentNetworkId:
        // temporary solution to unblock Network creation
        templateType === TemplateType.Network &&
        location.pathname.includes('/add')
          ? CANONICAL_NETWORK_ID
          : networkId,
    })

    if (_template && template?.id !== _template?.id) {
      dispatch(setEditorTemplate(_template))
    }
  }, [subtype, templates, template, location.pathname])

  useEffect(() => {
    return () => {
      dispatch(setEditorTemplate(null))
    }
  }, [types?.[0], subtype])

  const [queryTemplates] = useTemplatesLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: ({ templates }) => {
      dispatch(setTemplates(templates))
      setRetryCount(0) // Reset retry count on success
    },
    onError: error => {
      console.error('Unable to load templates', error)

      if (retryCount < MAX_RETRIES) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1)
          queryTemplates() // Retry the query
        }, RETRY_DELAY)
      } else {
        Modal.error({
          title: 'Templates failed to load',
          content: 'Please reload the page manually or contact support.',
        })
      }
    },
  })

  useEffect(() => {
    if (!isAuthenticated || !types?.length) {
      return
    }

    // temporary force purge cache
    if (!StorageHelper.getItem(LS_REVALIDATION_KEY)) {
      ;(async () => {
        StorageHelper.setItem(LS_REVALIDATION_KEY, 'true')
        await localforage.clear()
        window.location.reload()
      })()
    }

    dispatch(setTemplatesLoading(true))
    queryTemplates({
      variables: {
        where: {
          type: {
            in: types,
          },
          OR: [
            {
              networkId: {
                equals: networkId,
              },
            },
            {
              networkId: {
                equals: null,
              },
            },
          ],
        },
      },
    })
  }, [networkId, isAuthenticated, queryTemplates, dispatch, types, retryCount])

  useEffect(() => {
    // temporary solution to unblock Network creation
    if (
      types?.[0] === TemplateType.Network &&
      !networkId &&
      !templates?.find(
        template => template.network?.id === CANONICAL_NETWORK_ID,
      )
    ) {
      queryTemplates({
        variables: {
          where: {
            type: {
              in: types,
            },
            OR: [
              {
                networkId: {
                  equals: CANONICAL_NETWORK_ID,
                },
              },
              {
                networkId: {
                  equals: null,
                },
              },
            ],
          },
        },
      })
    }
  }, [networkId, isAuthenticated, queryTemplates, dispatch, types, templates])
}
